/**
 * API Configuration
 * 
 * Centralized configuration for API calls including:
 * - API base URL
 * - WebSocket URL
 * - API Key for authentication
 * - Common headers
 */

// API URLs (can be overridden by environment variables)
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:63871';
export const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:63871';

// API Key for client validation (set via environment variable)
// In development, this can be left as the default value
// In production, set REACT_APP_API_KEY to match the server's API_KEY
// CRITICAL: Always ensure this is a string, never undefined (undefined gets omitted by JSON.stringify)
const DEFAULT_API_KEY = 'coda_dev_api_key_change_in_production';
export const API_KEY = (process.env.REACT_APP_API_KEY && process.env.REACT_APP_API_KEY.trim()) || DEFAULT_API_KEY;

// Log API_KEY status in development (for debugging)
if (process.env.NODE_ENV === 'development') {
  console.log('🔑 Frontend API_KEY status:', {
    isSet: !!process.env.REACT_APP_API_KEY,
    isDefault: API_KEY === 'coda_dev_api_key_change_in_production',
    firstChars: API_KEY ? API_KEY.substring(0, 8) + '...' : 'undefined'
  });
}

/**
 * Get common headers for API requests
 * @param {boolean} includeAuth - Whether to include Authorization header
 * @returns {Object} - Headers object
 */
export function getApiHeaders(includeAuth = false) {
  const headers = {
    'Content-Type': 'application/json',
    'X-API-Key': API_KEY,
  };
  
  if (includeAuth) {
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  }
  
  return headers;
}

/**
 * Get WebSocket auth message with API key
 * @param {string} token - JWT token
 * @param {string} [language] - Language preference (optional)
 * @returns {Object} - Auth message object
 */
export function getWebSocketAuthMessage(token, language) {
  // CRITICAL: Always ensure apiKey is a string, never undefined or null
  // JSON.stringify() omits undefined properties, so we must always provide a string value
  // Use the DEFAULT_API_KEY if API_KEY is falsy (undefined, null, empty string, etc.)
  const apiKeyValue = (API_KEY && API_KEY.trim()) || DEFAULT_API_KEY;
  
  // Debug logging to verify API_KEY is set
  if (!API_KEY || API_KEY === DEFAULT_API_KEY || !API_KEY.trim()) {
    console.warn('⚠️ WARNING: API_KEY is not set or using default value!');
    console.warn('   This will cause WebSocket authentication to fail in production.');
    console.warn('   Set REACT_APP_API_KEY in .env.production and rebuild.');
    console.warn(`   Current API_KEY value: ${API_KEY === undefined ? 'undefined' : (API_KEY || 'empty string')}`);
  }
  
  // CRITICAL: Always create message with apiKey as a string (never undefined)
  // This ensures JSON.stringify() will always include the apiKey field
  const message = {
    type: 'auth',
    token: token || '', // Ensure token is never undefined
    apiKey: String(apiKeyValue), // Explicitly convert to string to prevent undefined
  };
  
  if (language) {
    message.language = language;
  }
  
  // Verify the message before returning
  if (!message.apiKey || message.apiKey === 'undefined') {
    console.error('❌ CRITICAL ERROR: apiKey is missing or undefined in auth message!');
    console.error('   This should never happen - apiKey should always be a string');
    message.apiKey = DEFAULT_API_KEY; // Force a value
  }
  
  // Log the message structure (without sensitive data) for debugging
  console.log('🔑 WebSocket auth message:', {
    type: message.type,
    hasToken: !!message.token,
    hasApiKey: !!message.apiKey,
    apiKeyLength: message.apiKey ? message.apiKey.length : 0,
    apiKeyPreview: message.apiKey ? message.apiKey.substring(0, 8) + '...' : 'undefined',
    apiKeyIsDefault: message.apiKey === DEFAULT_API_KEY,
    hasLanguage: !!message.language,
    fullMessage: JSON.stringify(message) // Show what's actually being sent
  });
  
  return message;
}

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint (relative to API_URL)
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} - Fetch response
 */
export async function apiRequest(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
  
  const headers = {
    ...getApiHeaders(true),
    ...options.headers,
  };
  
  return fetch(url, {
    ...options,
    headers,
  });
}

export default {
  API_URL,
  WS_URL,
  API_KEY,
  getApiHeaders,
  getWebSocketAuthMessage,
  apiRequest,
};

